import React, { useState, useEffect } from 'react';
import reportUsageService from '../../services/reportUsageService';
import { Card } from '../common';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell
} from 'recharts';
import './ReportUsageDashboard.css';

const COLORS = ['#3498db', '#2ecc71', '#e67e22', '#9b59b6', '#e74c3c', '#1abc9c'];

const CustomBarTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="ru-tooltip">
      <div className="ru-tooltip__label">{label}</div>
      <div>생성 수: <strong>{payload[0].value}건</strong></div>
    </div>
  );
};

const CustomPieTooltip = ({ active, payload }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="ru-tooltip">
      <div className="ru-tooltip__label">{payload[0].name}</div>
      <div><strong>{payload[0].value}건</strong></div>
    </div>
  );
};

const METRIC_LABELS = { cpu: 'CPU 사용률', memory: '메모리 사용률' };
const TYPE_LABELS = { min: '최소', average: '평균', max: '최대' };
const SERVICE_LABELS = { ec2: 'EC2', rds: 'RDS' };

const getSummaryTags = (col) => {
  if (!col) return [];
  const tags = [];
  if (col.services?.length > 0) tags.push(`서비스 ${col.services.length}개`);
  if (col.metrics?.length > 0) tags.push(`메트릭 ${col.metrics.length}개`);
  if (col.inspectionItems?.length > 0) tags.push(`검사 ${col.inspectionItems.length}개`);
  if (col.enableAIInspection) tags.push('AI 분석');
  return tags;
};

const DetailPanel = ({ collection }) => {
  if (!collection) return null;
  const { services, metrics, inspectionItems, enableAIInspection } = collection;
  const hasAny = services?.length || metrics?.length || inspectionItems?.length || enableAIInspection;
  if (!hasAny) return <div className="ru-detail__empty">수집 항목 없음</div>;

  return (
    <div className="ru-detail">
      {services?.length > 0 && (
        <div className="ru-detail__group">
          <div className="ru-detail__title">서비스 정보 수집</div>
          <div className="ru-detail__tags">
            {services.map(s => <span key={s} className="ru-tag">{s.toUpperCase()}</span>)}
          </div>
        </div>
      )}
      {metrics?.length > 0 && (
        <div className="ru-detail__group">
          <div className="ru-detail__title">메트릭 수집</div>
          <div className="ru-detail__list">
            {metrics.map((m, i) => (
              <div key={i} className="ru-detail__metric">
                <span className="ru-detail__metric-name">{m.name}</span>
                <span className="ru-tag">{SERVICE_LABELS[m.service] || m.service}</span>
                <span className="ru-tag">{METRIC_LABELS[m.metric] || m.metric}</span>
                <span className="ru-tag ru-tag--accent">{TYPE_LABELS[m.type] || m.type}</span>
              </div>
            ))}
          </div>
        </div>
      )}
      {inspectionItems?.length > 0 && (
        <div className="ru-detail__group">
          <div className="ru-detail__title">검사 항목 ({inspectionItems.length}개)</div>
          <div className="ru-detail__tags">
            {inspectionItems.map(id => <span key={id} className="ru-tag ru-tag--info">{id}</span>)}
          </div>
        </div>
      )}
      {enableAIInspection && (
        <div className="ru-detail__group">
          <div className="ru-detail__title">AI 분석</div>
          <span className="ru-tag ru-tag--ai">포함</span>
        </div>
      )}
    </div>
  );
};

const ReportUsageDashboard = ({ isAdmin = false }) => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [chartMode, setChartMode] = useState('daily');
  const [dateRange, setDateRange] = useState({ startDate: '', endDate: '' });
  const [expandedRow, setExpandedRow] = useState(null);

  useEffect(() => {
    if (!isAdmin) { setError('관리자 권한이 필요합니다.'); setLoading(false); return; }
    if (chartMode === 'daily' && dateRange.startDate && !dateRange.endDate) return;
    loadLogs();
  }, [dateRange, isAdmin, chartMode]);

  const loadLogs = async () => {
    try {
      setLoading(true); setError(null);
      const params = getDateParams();
      const response = await reportUsageService.getAllLogs(params);
      setLogs(response.data.logs || []);
    } catch (err) {
      setError('보고서 생성 로그를 불러오는데 실패했습니다.');
    } finally { setLoading(false); }
  };

  const handleDateRangeChange = (field, value) => {
    if (chartMode === 'daily') {
      setDateRange(prev => {
        const next = { ...prev, [field]: value };
        if (field === 'startDate') next.endDate = '';
        if (next.startDate && next.endDate) {
          const sm = next.startDate.substring(0, 7);
          const em = next.endDate.substring(0, 7);
          if (sm !== em) {
            const lastDay = new Date(sm.slice(0, 4), sm.slice(5, 7), 0).getDate();
            next.endDate = `${sm}-${String(lastDay).padStart(2, '0')}`;
          }
        }
        return next;
      });
    } else {
      setDateRange(prev => ({ ...prev, [field]: value }));
    }
  };

  const getDateParams = () => {
    const { startDate, endDate } = dateRange;
    const params = {};
    if (chartMode === 'monthly') {
      if (startDate) params.startDate = `${startDate}-01T00:00:00.000Z`;
      if (endDate) {
        const lastDay = new Date(endDate.slice(0, 4), endDate.slice(5, 7), 0).getDate();
        params.endDate = `${endDate}-${lastDay}T23:59:59.999Z`;
      }
    } else {
      if (startDate) params.startDate = `${startDate}T00:00:00.000Z`;
      if (endDate) params.endDate = `${endDate}T23:59:59.999Z`;
    }
    return params;
  };

  const handleChartModeChange = (mode) => {
    setChartMode(mode);
    setDateRange({ startDate: '', endDate: '' });
  };

  const toggleRow = (logId) => setExpandedRow(prev => prev === logId ? null : logId);

  // 통계
  const totalReports = logs.length;
  const uniqueUsers = new Set(logs.map(l => l.userId)).size;
  const uniqueArns = new Set(logs.map(l => l.roleArn)).size;
  const aiCount = logs.filter(l => l.collection?.enableAIInspection).length;

  const getBarChartData = () => {
    const grouped = {};
    logs.forEach(log => {
      const d = new Date(log.createdAt);
      const key = chartMode === 'daily'
        ? d.toLocaleDateString('ko-KR', { month: '2-digit', day: '2-digit' })
        : d.toLocaleDateString('ko-KR', { year: 'numeric', month: '2-digit' });
      grouped[key] = (grouped[key] || 0) + 1;
    });
    return Object.entries(grouped).map(([date, count]) => ({ date, count })).sort((a, b) => a.date.localeCompare(b.date));
  };

  const getUserPieData = () => {
    const grouped = {};
    logs.forEach(log => { const n = log.userName || log.userId; grouped[n] = (grouped[n] || 0) + 1; });
    return Object.entries(grouped).map(([name, value]) => ({ name, value })).sort((a, b) => b.value - a.value).slice(0, 6);
  };

  const getCollectionStats = () => {
    let service = 0, metric = 0, inspection = 0, ai = 0;
    logs.forEach(l => {
      if (l.collection?.services?.length > 0) service++;
      if (l.collection?.metrics?.length > 0) metric++;
      if (l.collection?.inspectionItems?.length > 0) inspection++;
      if (l.collection?.enableAIInspection) ai++;
    });
    return [
      { name: '서비스 정보', value: service },
      { name: '메트릭', value: metric },
      { name: '검사 항목', value: inspection },
      { name: 'AI 분석', value: ai }
    ].filter(d => d.value > 0);
  };

  if (loading) return <div className="ru-dashboard"><div className="ru-loading">보고서 생성 로그를 불러오는 중...</div></div>;
  if (error) return <div className="ru-dashboard"><div className="ru-error">{error}</div></div>;

  return (
    <div className="ru-dashboard">
      <div className="ru-header">
        <h2>보고서 생성 히스토리</h2>
        <div className="ru-date-selector">
          {chartMode === 'monthly' ? (
            <>
              <label>시작 월</label>
              <input type="month" value={dateRange.startDate} onChange={e => handleDateRangeChange('startDate', e.target.value)} />
              <span>~</span>
              <label>종료 월</label>
              <input type="month" value={dateRange.endDate} onChange={e => handleDateRangeChange('endDate', e.target.value)} />
            </>
          ) : (
            <>
              <label>시작일</label>
              <input type="date" value={dateRange.startDate} onChange={e => handleDateRangeChange('startDate', e.target.value)} />
              <span>~</span>
              <label>종료일</label>
              <input
                type="date"
                value={dateRange.endDate}
                min={dateRange.startDate}
                max={dateRange.startDate ? `${dateRange.startDate.substring(0, 7)}-${String(new Date(dateRange.startDate.slice(0,4), dateRange.startDate.slice(5,7), 0).getDate()).padStart(2,'0')}` : ''}
                onChange={e => handleDateRangeChange('endDate', e.target.value)}
              />
            </>
          )}
        </div>
      </div>

      <div className="ru-summary">
        <Card className="ru-summary-card"><h3>총 생성 수</h3><div className="ru-stat">{totalReports}</div></Card>
        <Card className="ru-summary-card"><h3>사용자 수</h3><div className="ru-stat">{uniqueUsers}</div></Card>
        <Card className="ru-summary-card"><h3>고객사 수</h3><div className="ru-stat">{uniqueArns}</div></Card>
        <Card className="ru-summary-card"><h3>AI 분석 포함</h3><div className="ru-stat">{aiCount}</div></Card>
      </div>

      <div className="ru-charts">
        <Card className="ru-chart-card ru-chart-card--wide">
          <div className="ru-chart-header">
            <h3>생성 추이</h3>
            <div className="ru-chart-toggle">
              <button className={chartMode === 'daily' ? 'active' : ''} onClick={() => handleChartModeChange('daily')}>일별</button>
              <button className={chartMode === 'monthly' ? 'active' : ''} onClick={() => handleChartModeChange('monthly')}>월별</button>
            </div>
          </div>
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={getBarChartData()} margin={{ top: 10, right: 20, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e1e8ed" />
              <XAxis dataKey="date" tick={{ fontSize: 12 }} />
              <YAxis tick={{ fontSize: 12 }} allowDecimals={false} />
              <Tooltip content={<CustomBarTooltip />} />
              <Bar dataKey="count" fill="#3498db" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </Card>

        <Card className="ru-chart-card">
          <h3>사용자별 생성</h3>
          {getUserPieData().length > 0 ? (
            <ResponsiveContainer width="100%" height={220}>
              <PieChart>
                <Pie data={getUserPieData()} cx="50%" cy="50%" outerRadius={80} dataKey="value" label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}>
                  {getUserPieData().map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                </Pie>
                <Tooltip content={<CustomPieTooltip />} />
              </PieChart>
            </ResponsiveContainer>
          ) : <div className="ru-no-data">데이터 없음</div>}
        </Card>

        <Card className="ru-chart-card">
          <h3>수집 항목 분포</h3>
          {getCollectionStats().length > 0 ? (
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={getCollectionStats()} layout="vertical" margin={{ top: 10, right: 20, left: 60, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e1e8ed" />
                <XAxis type="number" tick={{ fontSize: 12 }} allowDecimals={false} />
                <YAxis type="category" dataKey="name" tick={{ fontSize: 12 }} />
                <Tooltip />
                <Bar dataKey="value" fill="#2ecc71" radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          ) : <div className="ru-no-data">데이터 없음</div>}
        </Card>
      </div>

      {/* 히스토리 */}
      <Card className="ru-history">
        <h3>생성 히스토리 (최신순)</h3>
        {logs.length === 0 ? (
          <div className="ru-no-data">생성 로그가 없습니다.</div>
        ) : (
          <div className="ru-log-list">
            {logs.map(log => {
              const isOpen = expandedRow === log.logId;
              const tags = getSummaryTags(log.collection);
              return (
                <div key={log.logId} className={`ru-log-item ${isOpen ? 'ru-log-item--open' : ''}`}>
                  <div className="ru-log-item__row" onClick={() => toggleRow(log.logId)}>
                    <div className="ru-log-item__cell ru-log-item__date">
                      {new Date(log.createdAt).toLocaleString('ko-KR')}
                    </div>
                    <div className="ru-log-item__cell ru-log-item__user" title={log.userId}>
                      {log.userName || '-'}
                    </div>
                    <div className="ru-log-item__cell ru-log-item__arn" title={log.roleArn}>
                      {log.roleArn || '-'}
                    </div>
                    <div className="ru-log-item__cell ru-log-item__regions">
                      {(log.regions || []).map(r => <span key={r} className="ru-tag">{r}</span>)}
                    </div>
                    <div className="ru-log-item__cell ru-log-item__summary">
                      {tags.map((t, i) => <span key={i} className="ru-tag ru-tag--info">{t}</span>)}
                    </div>
                    <div className="ru-log-item__cell ru-log-item__toggle">
                      <button className={`ru-expand-btn ${isOpen ? 'ru-expand-btn--open' : ''}`}>
                        <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                          <path d="M3 4.5L6 7.5L9 4.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                      </button>
                    </div>
                  </div>
                  {isOpen && (
                    <div className="ru-log-item__detail">
                      <DetailPanel collection={log.collection} />
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </Card>
    </div>
  );
};

export default ReportUsageDashboard;
